import type { CopilotProposal } from "@repo/app-types/entities/copilotProposal";
import {
  ToolType,
  type Tool,
  type ToolPatchPayload,
} from "@repo/app-types/entities/tool";
import type { CodeToolSecretListing } from "@repo/app-types/entities/tools/code";
import { Size } from "@repo/ui/helpers/helpers";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { Label } from "@repo/ui/Label/label";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { useEffect, useMemo, useState, type FC } from "react";
import { useNavigate, useParams } from "react-router";
import { useResolveProposalMutation } from "../api/entities/copilotProposal.js";
import {
  useGetToolQuery,
  useUpdateToolMutation,
} from "../api/entities/tool.js";
import { getApiErrorMessage } from "../api/helpers/errors.js";
import { isConflictQueryError } from "../api/queryErrors.js";
import { appPaths } from "../app/appPaths.js";
import { PageBody, PageLayout } from "../components/layout/pageLayout.js";
import { useDeepLinkedProposal } from "../features/copilot/copilotDeepLinks.js";
import {
  useReportEditorProposal,
  type EditorFocus,
} from "../features/copilot/editorProposalsSlice.js";
import {
  NotificationType,
  showNotification,
} from "../features/notificationsOverlay/notificationsSlice.js";
import { CodeToolTestPanel } from "../features/tools/codeToolTestPanel.js";
import { ToolFormActions } from "../features/tools/components/toolFormActions.js";
import { useToolForm } from "../features/tools/hooks/useToolForm.js";
import { ToolMoreMenu } from "../features/tools/toolMoreMenu.js";
import { ToolProposalBanner } from "../features/tools/toolProposalBanner.js";
import { useAppDispatch } from "../hooks/hook.js";

export const ToolDetailsPage: FC = () => {
  const { toolId } = useParams();
  if (!toolId) {
    throw new Error("Missing tool ID");
  }

  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetToolQuery(toolId);
  const tool = data?.tool;

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Wide}
        title={tool?.name ?? "Tool"}
        isLoading={isLoading}
        actions={
          tool && (
            <ToolMoreMenu
              tool={tool}
              toolId={toolId}
              onToolDeleted={() => navigate(appPaths.agentTools.build())}
            />
          )
        }
      />
      <PageBody width={PageWidth.Wide}>
        {isError && <div>Failed to load tool.</div>}
        {data ? (
          // Keyed by tool: the form keeps its unsaved edits in state, which
          // must not carry over to another tool.
          <ToolConfiguration
            key={data.tool.id}
            tool={data.tool}
            secrets={data.secrets}
          />
        ) : null}
      </PageBody>
    </PageLayout>
  );
};

interface ToolConfigurationProps {
  tool: Tool;
  secrets: readonly CodeToolSecretListing[];
}

const ToolConfiguration: FC<ToolConfigurationProps> = (props) => {
  const { tool, secrets } = props;
  const dispatch = useAppDispatch();

  const [updateTool, { reset: resetUpdate }] = useUpdateToolMutation();
  const [resolveProposal] = useResolveProposalMutation();

  // The Ask Alpharun proposal in the unsaved changes, if any. Save applies it.
  const [linkedProposal, setLinkedProposal] =
    useState<CopilotProposal<"tool"> | null>(null);

  // Records the decision and unlinks the proposal either way: once the form
  // is saved or discarded there is nothing left to retry it with. One that
  // has moved on (409) was already decided.
  const resolveLinkedProposal = async (
    proposal: CopilotProposal<"tool">,
    status: "applied" | "discarded",
  ) => {
    setLinkedProposal((current) => (current === proposal ? null : current));
    try {
      await resolveProposal({ proposalId: proposal.id, status }).unwrap();
    } catch (error) {
      if (!isConflictQueryError(error)) {
        console.error("Failed to resolve the suggestion:", error);
        showNotification(dispatch, {
          id: `tool-proposal-resolve-error-${Date.now().toString()}`,
          type: NotificationType.FAILURE,
          primaryMessage: "Failed to update the suggestion",
          secondaryMessage: "It stays open in Ask Alpharun.",
        });
      }
    }
  };

  const handleSave = async (payload: ToolPatchPayload) => {
    try {
      await updateTool({
        toolId: tool.id,
        payload,
      }).unwrap();

      showNotification(dispatch, {
        id: `tool-update-${new Date().getTime().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Tool updated",
        secondaryMessage: "Tool has been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to update tool:", error);
      showNotification(dispatch, {
        id: `tool-update-error-${new Date().getTime().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to update tool",
        secondaryMessage: getApiErrorMessage(error, "Please try again."),
      });

      // Rethrow so the form keeps the user's changes
      throw error;
    } finally {
      // The arguments can hold secret values; don't leave them in the store.
      resetUpdate();
    }
    // The saved settings carried the suggestion, so it is applied.
    // TODO: Apply the proposal in the save's transaction on the server instead.
    if (linkedProposal) {
      await resolveLinkedProposal(linkedProposal, "applied");
    }
  };

  // No-op for edit page - the forms handle discard internally
  const handleCancel = () => {};

  const {
    isEditMode,
    isFormValid,
    hasChanges,
    isSaving,
    onSave,
    onDiscard,
    renderForm,
    prepareSettings,
    applySaveError,
    parameters,
    testInput,
    onTestInputChange,
    mergeChanges,
    resetCount,
  } = useToolForm({
    type: tool.type,
    initialData: tool,
    initialSecrets: secrets,
    onSave: handleSave,
  });

  const handleDiscard = () => {
    onDiscard();
    if (linkedProposal) {
      void resolveLinkedProposal(linkedProposal, "discarded");
    }
  };

  const showProposalUnavailable = () =>
    showNotification(dispatch, {
      id: `tool-proposal-unavailable-${Date.now().toString()}`,
      type: NotificationType.FAILURE,
      primaryMessage: "This suggestion is no longer available.",
    });

  // A proposal's changes are merged over the user's other unsaved edits. It
  // stays pending until Save writes them, which applies it. One drafted for
  // another type no longer fits the tool.
  const putProposalIntoDraft = useEffectEvent(
    (proposal: CopilotProposal<"tool">) => {
      const { data } = proposal;
      if (data.tool_type !== tool.type) {
        showProposalUnavailable();
        return;
      }
      mergeChanges({
        name: data.name,
        description: data.description,
        settings: data.settings,
      });
      setLinkedProposal(proposal);
    },
  );

  // One-shot link from an Ask Alpharun proposal card (?proposalId=...): the
  // proposal goes into the form as soon as it loads. One for another tool
  // loads nothing.
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("tool", { onUnavailable: showProposalUnavailable });
  // A proposal that arrives during a save waits for it to finish: the save
  // clears the form's changes when it succeeds.
  const [arrivedProposal, setArrivedProposal] =
    useState<CopilotProposal<"tool"> | null>(null);
  useEffect(() => {
    if (!deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    if (deepLinkProposal.resource_id === tool.id) {
      setArrivedProposal(deepLinkProposal);
    }
  }, [deepLinkProposal, clearDeepLinkProposalId, tool.id]);
  useEffect(() => {
    if (arrivedProposal && !isSaving) {
      setArrivedProposal(null);
      putProposalIntoDraft(arrivedProposal);
    }
  }, [arrivedProposal, isSaving, putProposalIntoDraft]);

  const hasLinkedProposal = linkedProposal !== null;
  const editorFocus = useMemo(
    (): EditorFocus => ({
      resource_type: "tool",
      resourceId: tool.id,
      hasUnsavedChanges: hasChanges,
      hasLinkedProposal,
    }),
    [tool.id, hasChanges, hasLinkedProposal],
  );
  useReportEditorProposal(linkedProposal?.id, isSaving, editorFocus);

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full rounded-lg border border-border-light bg-white">
        <div className="border-b border-border-light p-4">
          <Label size={Size.MEDIUM}>Tool settings</Label>
          <p className="mt-1 text-xs text-muted-foreground">
            Configure the settings for this tool. To manage its usage within an
            agent, go to the agent&apos;s settings. To provide guidance for when
            the tool should be used, add context to your Playbook.
          </p>
        </div>

        <div className="flex w-full flex-col gap-3 p-4">
          {linkedProposal ? (
            <ToolProposalBanner data={linkedProposal.data} tool={tool} />
          ) : null}

          {renderForm()}

          <ToolFormActions
            isEditMode={isEditMode}
            hasChanges={hasChanges}
            isFormValid={isFormValid}
            isSaving={isSaving}
            onSave={onSave}
            onDiscard={handleDiscard}
            onCancel={handleCancel}
          />
        </div>
      </div>
      {tool.type === ToolType.CODE && testInput ? (
        <CodeToolTestPanel
          tool={tool}
          parameters={parameters ?? []}
          prepareSettings={prepareSettings}
          applySaveError={applySaveError}
          testInput={testInput}
          onTestInputChange={onTestInputChange}
          formResetCount={resetCount}
          isReadOnly={isSaving}
        />
      ) : null}
    </div>
  );
};
